import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Stack, Text, Menu } from "@mantine/core";
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconDots,
  IconSearch,
  IconUserOff,
  IconUserCheck,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import "./DownlineTab.css";

// Avatar hues, picked per rep id so a rep keeps their color
const HUES = ["#d0521f", "#3b4da8", "#7550a8", "#28a745", "#b5832a", "#3b85c8"];
const hueFor = id => HUES[Math.abs(Number(id) || 0) % HUES.length];

const fullName = rep => `${rep.first_name || ""} ${rep.last_name || ""}`.trim();
const firstName = name => name.split(/\s+/)[0] || "";
const initials = name =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0] || "")
    .join("")
    .toUpperCase();

// Everyone at or below `reps`, depth first
const flatten = reps => reps.flatMap(rep => [rep, ...flatten(rep.reps || [])]);

// Everyone dropped anywhere in the tree; they're listed under the manager
// they were last under
const collectDropped = node => [
  ...(node.dropped_off_reps || []),
  ...(node.reps || []).flatMap(collectDropped),
];

function Avatar({ rep, name, size = 40, onClick }) {
  const content = rep?.profile_image_url ? (
    <img src={rep.profile_image_url} alt="" />
  ) : (
    initials(name)
  );
  const style = {
    width: size,
    height: size,
    background: hueFor(rep?.rep_id),
    fontSize: size * 0.36,
  };
  if (!onClick) {
    return (
      <span className="downline-tab-avatar" style={style} aria-hidden="true">
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="downline-tab-avatar"
      style={style}
      onClick={onClick}
      aria-label={`Open ${name}'s profile`}
    >
      {content}
    </button>
  );
}

/// The Org tab's Downline view, the web version of iOS's DownlineRepTableView
/// in downline-only mode: the caller's tree from GET /v2/rep/downline, one
/// level at a time. Rows with a team drill in; the avatar (or a row with no
/// team) opens the rep's profile.
function DownlineTab() {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [root, setRoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // rep_ids from the top of the tree down to the rep on screen ([] = root)
  const [path, setPath] = useState([]);
  // "total" is the tree; "dropped" is the Dropped Off Reps report
  const [report, setReport] = useState("total");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeSearch, setScopeSearch] = useState("");
  const [search, setSearch] = useState("");
  const [dropCandidate, setDropCandidate] = useState(null);
  const [busy, setBusy] = useState(false);
  const headerRef = useRef(null);

  const isCompanyAdmin = user?.role === "COMPANY_ADMIN";
  const myName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

  const load = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const response = await fetch(getApiEndpoint("/v2/rep/downline"), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Couldn't load downline");
      setRoot((await response.json()).data);
      setError(null);
    } catch (err) {
      setError(err.message || "Couldn't load downline");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT]);

  useEffect(() => {
    load();
  }, [load]);

  // Close the header's dropdowns on an outside click
  useEffect(() => {
    if (!menuOpen && !scopeOpen) return undefined;
    const close = event => {
      if (!headerRef.current?.contains(event.target)) {
        setMenuOpen(false);
        setScopeOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen, scopeOpen]);

  // Top-level reps, minus the caller in case they come back in their own tree
  const topLevel = useMemo(
    () =>
      (root?.reps || []).filter(
        rep => Number(rep.rep_id) !== Number(user?.repId)
      ),
    [root, user]
  );

  const index = useMemo(() => {
    const byId = new Map();
    flatten(topLevel).forEach(rep => byId.set(rep.rep_id, rep));
    return byId;
  }, [topLevel]);

  const dropped = useMemo(
    () =>
      (root ? collectDropped(root) : []).sort((a, b) =>
        fullName(a).localeCompare(fullName(b))
      ),
    [root]
  );

  // Path entries that still resolve after a reload
  const validPath = path.filter(id => index.has(id));
  const focus = validPath.length
    ? index.get(validPath[validPath.length - 1])
    : null;
  const kids = focus ? focus.reps || [] : topLevel;
  const scopeReps = flatten(kids);

  const rootLabel = isCompanyAdmin ? "My Company" : "My Reps";
  // At the root the crumb names whose downline this is ("Zack's Downline")
  const rootCrumb = (() => {
    if (isCompanyAdmin || validPath.length || !myName) return rootLabel;
    const first = firstName(myName);
    return `${first}${first.endsWith("s") ? "'" : "'s"} Downline`;
  })();

  const totalTitle = isCompanyAdmin ? "Total Company" : "Total Downline";
  let title;
  if (report === "dropped") title = "Dropped Off Reps";
  else if (focus) title = fullName(focus);
  else title = isCompanyAdmin ? "My Company" : "Total Reps";

  const headline = report === "dropped" ? dropped.length : scopeReps.length;

  // A search looks through everyone below the rep on screen, not just one level
  const query = search.trim().toLowerCase();
  const tableReps = (() => {
    const base = report === "dropped" ? dropped : query ? scopeReps : kids;
    if (!query) return base;
    return base.filter(rep => fullName(rep).toLowerCase().includes(query));
  })();

  // Ancestor chain down to `id`, so picking someone leaves the same crumbs a
  // row click would
  const pathTo = id => {
    const walk = (reps, trail) => {
      for (const rep of reps) {
        const next = [...trail, rep.rep_id];
        if (rep.rep_id === id) return next;
        const hit = walk(rep.reps || [], next);
        if (hit) return hit;
      }
      return null;
    };
    return walk(topLevel, []) || [];
  };

  const drillTo = nextPath => {
    setPath(nextPath);
    setReport("total");
    setSearch("");
  };

  const openProfile = rep => navigate(`/reps/profile/${rep.user_id}`);

  const setDeactivated = async (rep, deactivated, cascade) => {
    setBusy(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const response = await fetch(
        getApiEndpoint(`/v2/rep/${rep.user_id}/deactivated`),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ deactivated, drop_cascade: cascade }),
        }
      );
      if (!response.ok) {
        let message = deactivated
          ? "Couldn't drop rep"
          : "Couldn't restore rep";
        try {
          const data = await response.json();
          if (data.message) message = data.message;
        } catch {
          // keep the default
        }
        throw new Error(message);
      }
      showSuccess(
        deactivated
          ? `${fullName(rep)} marked as dropped`
          : `${fullName(rep)} restored`
      );
      await load();
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
      setDropCandidate(null);
    }
  };

  if (loading && !root) {
    return <div className="downline-tab-state">Loading downline...</div>;
  }
  if (error && !root) {
    return (
      <div className="downline-tab-state downline-tab-state-error">
        {error}
        <button
          type="button"
          className="downline-tab-retry"
          onClick={() => {
            setLoading(true);
            load();
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  const scopeQuery = scopeSearch.trim().toLowerCase();
  const scopePeople = [...index.values()]
    .filter(
      rep => !scopeQuery || fullName(rep).toLowerCase().includes(scopeQuery)
    )
    .sort((a, b) => fullName(a).localeCompare(fullName(b)));

  const crumbs = [
    { label: rootCrumb, target: [] },
    ...validPath.slice(0, -1).map((id, i) => ({
      label: firstName(fullName(index.get(id))),
      target: validPath.slice(0, i + 1),
    })),
  ];

  const renderHeader = () => (
    <div className="downline-tab-header" ref={headerRef}>
      <div className="downline-tab-header-row">
        {focus ? (
          <Avatar rep={focus} name={fullName(focus)} size={52} />
        ) : (
          <span
            className="downline-tab-avatar"
            style={{
              width: 52,
              height: 52,
              background: hueFor(user?.repId),
              fontSize: 19,
            }}
            aria-hidden="true"
          >
            {user?.profileImageUrl ? (
              <img src={user.profileImageUrl} alt="" />
            ) : (
              initials(myName || rootLabel)
            )}
          </span>
        )}

        <div className="downline-tab-ident">
          <div className="downline-tab-crumbs">
            {validPath.length > 0 && (
              <button
                type="button"
                className="downline-tab-back"
                onClick={() => drillTo(validPath.slice(0, -1))}
                aria-label="Back"
              >
                <IconChevronLeft size={18} stroke={2.5} />
              </button>
            )}
            {crumbs.map((crumb, i) => (
              <React.Fragment key={crumb.target.join("-") || "root"}>
                {i > 0 && <span className="downline-tab-crumb-dot">·</span>}
                <button
                  type="button"
                  className="downline-tab-crumb"
                  onClick={() => drillTo(crumb.target)}
                  disabled={validPath.length === 0}
                >
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button
            type="button"
            className="downline-tab-title"
            onClick={() => {
              setMenuOpen(false);
              setScopeOpen(open => !open);
            }}
            aria-expanded={scopeOpen}
          >
            {title}
          </button>
          {focus && report === "total" && focus.role_name && (
            <span className="downline-tab-role">{focus.role_name}</span>
          )}
        </div>

        <button
          type="button"
          className="downline-tab-headline"
          onClick={() => {
            setScopeOpen(false);
            setMenuOpen(open => !open);
          }}
          aria-expanded={menuOpen}
          aria-label={`${headline}. Choose a report.`}
        >
          <span className="downline-tab-headline-value">{headline}</span>
          <IconChevronRight
            size={20}
            stroke={2.5}
            className={menuOpen ? "downline-tab-rotate" : undefined}
          />
        </button>
      </div>

      {menuOpen && (
        <ul className="downline-tab-dropdown">
          {[
            { id: "total", label: totalTitle },
            { id: "dropped", label: "Dropped Off Reps" },
          ].map(option => (
            <li key={option.id}>
              <button
                type="button"
                className="downline-tab-option"
                onClick={() => {
                  setReport(option.id);
                  setSearch("");
                  setMenuOpen(false);
                }}
              >
                <span>{option.label}</span>
                {report === option.id && <IconCheck size={16} />}
              </button>
            </li>
          ))}
        </ul>
      )}

      {scopeOpen && (
        <div className="downline-tab-dropdown">
          <div className="downline-tab-scope-search">
            <IconSearch size={15} aria-hidden="true" />
            <input
              type="search"
              placeholder="Search people"
              value={scopeSearch}
              onChange={e => setScopeSearch(e.target.value)}
              aria-label="Search people"
              autoFocus
            />
          </div>
          <ul className="downline-tab-scope-list">
            {!scopeQuery && (
              <li>
                <button
                  type="button"
                  className="downline-tab-option"
                  onClick={() => {
                    drillTo([]);
                    setScopeOpen(false);
                  }}
                >
                  <span>{rootLabel}</span>
                  <span className="downline-tab-option-count">
                    {flatten(topLevel).length}
                  </span>
                  {validPath.length === 0 && <IconCheck size={16} />}
                </button>
              </li>
            )}
            {scopePeople.map(rep => (
              <li key={rep.rep_id}>
                <button
                  type="button"
                  className="downline-tab-option"
                  onClick={() => {
                    drillTo(pathTo(rep.rep_id));
                    setScopeOpen(false);
                    setScopeSearch("");
                  }}
                >
                  <Avatar rep={rep} name={fullName(rep)} size={26} />
                  <span>{fullName(rep)}</span>
                  {rep.rep_count > 0 && (
                    <span className="downline-tab-option-count">
                      {rep.rep_count}
                    </span>
                  )}
                  {focus?.rep_id === rep.rep_id && <IconCheck size={16} />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderRow = rep => {
    const name = fullName(rep);
    const isDropped = report === "dropped";
    const direct = (rep.reps || []).length;
    const total = rep.rep_count ?? flatten(rep.reps || []).length;
    const hasTeam = !isDropped && direct > 0;
    const droppedOn = rep.deactivated_at
      ? new Date(rep.deactivated_at).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

    return (
      <li key={rep.user_id ?? rep.rep_id} className="downline-tab-row">
        <Avatar rep={rep} name={name} onClick={() => openProfile(rep)} />
        <button
          type="button"
          className="downline-tab-row-main"
          onClick={() =>
            hasTeam ? drillTo([...validPath, rep.rep_id]) : openProfile(rep)
          }
        >
          <span className="downline-tab-row-ident">
            <span className="downline-tab-name">
              {query || isDropped ? name : firstName(name)}
            </span>
            <span className="downline-tab-sub">
              {isDropped
                ? [droppedOn && `Dropped ${droppedOn}`, rep.deactivation_reason]
                    .filter(Boolean)
                    .join(" · ") || rep.role_name
                : rep.role_name}
            </span>
          </span>
          {!isDropped && (
            <span className="downline-tab-total">
              <span
                className={`downline-tab-total-value${
                  total > 0 ? "" : " is-empty"
                }`}
              >
                {total > 0 ? total : "—"}
              </span>
              <span className="downline-tab-total-sub">
                {direct > 0 ? `${direct} direct` : " "}
              </span>
            </span>
          )}
          <span className="downline-tab-chevron" aria-hidden="true">
            {hasTeam && <IconChevronRight size={16} stroke={2.25} />}
          </span>
        </button>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <button
              type="button"
              className="downline-tab-more"
              aria-label={`More for ${name}`}
            >
              <IconDots size={18} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>
            {isDropped ? (
              <Menu.Item
                leftSection={<IconUserCheck size={16} />}
                onClick={() => setDeactivated(rep, false, false)}
                disabled={busy}
              >
                Restore
              </Menu.Item>
            ) : (
              <Menu.Item
                color="red"
                leftSection={<IconUserOff size={16} />}
                onClick={() => setDropCandidate(rep)}
              >
                Mark as dropped
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </li>
    );
  };

  let emptyText = "No downline — individual producer";
  if (report === "dropped") emptyText = "No dropped off reps";
  if (query) emptyText = "No reps match";

  return (
    <div className="downline-tab">
      {renderHeader()}

      <div className="downline-tab-search">
        <IconSearch size={16} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search reps"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search reps"
        />
      </div>

      <div className="downline-tab-table">
        <div className="downline-tab-table-head">
          <span>Rep</span>
          {report !== "dropped" && (
            <span className="downline-tab-head-total">Total reps</span>
          )}
        </div>
        {tableReps.length === 0 ? (
          <div className="downline-tab-empty">{emptyText}</div>
        ) : (
          <ul className="downline-tab-rows">{tableReps.map(renderRow)}</ul>
        )}
      </div>

      <Modal
        opened={Boolean(dropCandidate)}
        onClose={() => setDropCandidate(null)}
        title={
          dropCandidate ? `Mark ${fullName(dropCandidate)} as dropped?` : ""
        }
        centered
        size="md"
      >
        {dropCandidate && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Dropped reps are hidden from every reps page company-wide. You can
              restore them anytime from Dropped Off Reps.
            </Text>
            {(dropCandidate.reps || []).length > 0 ? (
              <>
                <Button
                  color="red"
                  loading={busy}
                  onClick={() => setDeactivated(dropCandidate, true, true)}
                >
                  Drop & hide their whole team
                </Button>
                <Button
                  color="red"
                  variant="light"
                  disabled={busy}
                  onClick={() => setDeactivated(dropCandidate, true, false)}
                >
                  Drop only {dropCandidate.first_name}, keep their team
                </Button>
              </>
            ) : (
              <Button
                color="red"
                loading={busy}
                onClick={() => setDeactivated(dropCandidate, true, false)}
              >
                Drop {dropCandidate.first_name}
              </Button>
            )}
            <Button
              variant="default"
              onClick={() => setDropCandidate(null)}
              disabled={busy}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </Modal>
    </div>
  );
}

export default DownlineTab;
